import STError from 'supertokens-web-js/lib/build/error';
import { type User } from 'supertokens-web-js/lib/build/types';
import {
  createCode,
  consumeCode,
  resendCode,
  clearLoginAttemptInfo,
} from 'supertokens-web-js/recipe/passwordless';
import Session from 'supertokens-web-js/recipe/session';

export const sendOTP = async (
  email: string,
): Promise<{ status: 'OK' | 'SIGN_IN_UP_NOT_ALLOWED' }> => {
  try {
    const response = await createCode({
      email,
    });
    return { status: response.status };
  } catch (err) {
    if (STError.isThisError(err)) {
      // Here we can check custom errors, like no scans connected to the email.
    }
    throw new Error('Something went wrong.');
  }
};

export const validateOTP = async (
  otp: string,
): Promise<
  | {
      status: 'OK';
      createdNewUser: boolean;
      user: User;
    }
  | {
      status: 'INCORRECT_USER_INPUT_CODE';
      failedAttempts: number;
      maxAttempts: number;
    }
  | {
      status: 'EXPIRED_USER_INPUT_CODE';
    }
  | {
      status: 'RESTART_FLOW';
    }
> => {
  try {
    const response = await consumeCode({
      userInputCode: otp,
    });
    if (response.status === 'OK') {
      return {
        status: 'OK',
        createdNewUser: response.createdNewRecipeUser,
        user: response.user,
      };
    }
    if (response.status === 'INCORRECT_USER_INPUT_CODE_ERROR') {
      return {
        status: 'INCORRECT_USER_INPUT_CODE',
        failedAttempts: response.failedCodeInputAttemptCount,
        maxAttempts: response.maximumCodeInputAttempts,
      };
    }
    if (response.status === 'EXPIRED_USER_INPUT_CODE_ERROR') {
      return {
        status: 'EXPIRED_USER_INPUT_CODE',
      };
    }
    if (response.status === 'RESTART_FLOW_ERROR') {
      return {
        status: 'RESTART_FLOW',
      };
    }
    throw new Error('Login failed. Please try again');
  } catch (err) {
    if (STError.isThisError(err)) {
      // Here we can check custom errors, like no scans connected to the email.
    }
    throw new Error('Something went wrong.');
  }
};

export const resendOTP = async (): Promise<
  | {
      status: 'RESTART_FLOW';
    }
  | {
      status: 'OK';
    }
> => {
  try {
    const response = await resendCode();

    if (response.status === 'OK') {
      return {
        status: 'OK',
      };
    }

    if (response.status === 'RESTART_FLOW_ERROR') {
      // this can happen if the user has already successfully logged in into
      // another device whilst also trying to login to this one.
      return {
        status: 'RESTART_FLOW',
      };
    }
    throw new Error('Login failed. Please try again');
  } catch (err) {
    if (STError.isThisError(err)) {
      // Here we can check custom errors, like no scans connected to the email.
    }
    throw new Error('Something went wrong.');
  }
};

export const cancelOtpSession = async (): Promise<void> =>
  clearLoginAttemptInfo();

export const doesSessionExist = async (): Promise<boolean> =>
  Session.doesSessionExist();

export const getToken = async (): Promise<string> => {
  const accessToken = await Session.getAccessToken();
  return accessToken || '';
};

export const logout = async (): Promise<void> => {
  await Session.signOut();
};
